import React from "react";
import { IPage } from "../../../api/entities/page";
import { IShardRepositoryRequestParams, IShardRepository, ShardRepositorySortField } from "../../../api/entities/shard-repository";
import { useNavigate } from "react-router-dom";
import { routeUtils } from "../../../utils/route-utils";
import { storageTypeToNameMap, storageUtils } from "../../../utils/storage-utils";
import Table, { IColumnDefinition, TableSelected } from "../../shared-ui/table/table";
import styles from "../../shared-ui/table/table.module.scss";
import { MoreOption } from "./moreoption";
import { IAppState } from "../../../redux/reducers";
import { connect } from "react-redux";
import { getOwnerColumn } from "../../common/sections/owner-column/owner-column";
import { AccountType } from "../../../api/entities/account";

const mapStateToProps = (state: IAppState) => ({
    accountType: state.users.currentUser.accountType,
});

export interface IShardRepositoryTableProps
    extends ReturnType<typeof mapStateToProps> {
    isLoading: boolean;
    page: IPage<IShardRepository>;
    requestParams: IShardRepositoryRequestParams;
    setRequestParams: (data: Partial<IShardRepositoryRequestParams>) => void;

    selection?: TableSelected<IShardRepository>;
    onSelectionChanged?: (selection: TableSelected<IShardRepository>) => void;
    onSelectAll?: () => void;

    isRowClickable?: boolean;
    hasManageColumn?: boolean;
}

export const ShardRepositoryTable = connect(mapStateToProps, null)((props: IShardRepositoryTableProps) => {
    const navigate = useNavigate();
    const columns: IColumnDefinition<IShardRepository, ShardRepositorySortField>[] = [
        {
            sortKey: ShardRepositorySortField.Type,
            label: "Type",
            formatter: row => storageTypeToNameMap[storageUtils.getStorageProviderTypeGeneralizedType(row.storageType)]
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
        {
            sortKey: ShardRepositorySortField.Bucket,
            label: "Bucket / Container",
            formatter: row => row.bucket
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
        {
            sortKey: ShardRepositorySortField.Region,
            label: "Region",
            formatter: row => row.region
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
        {
            label: "Attachment Count",
            formatter: row => row.attachmentCount ?? 0,
        } as IColumnDefinition<IShardRepository, ShardRepositorySortField>,
    ];

    if (props.accountType === AccountType.Enterprise) {
        columns.push(getOwnerColumn(null));
    }

    if (props.hasManageColumn) {
        columns.push(
            {
                label: "Manage",
                className: styles["col--manage"],
                formatter: row => (<MoreOption data={row}/>),
            });
    }

    return (
        <Table
            testId="shard-repository-table"
            hideCheckbox={!props.selection}
            columns={columns}
            rows={props.page.data}
            totalRows={props.page.count}
            loading={props.isLoading}
            requestParams={props.requestParams}
            onRequestParamsChanged={props.setRequestParams}
            onRowClick={row => !!props.isRowClickable && navigate(routeUtils.getRouteToShardRepositoryDetails(row.id.toString()))}
            responsive={true}

            selected={props.selection}
            onSetSelected={props.onSelectionChanged}
            onSelectAll={props.onSelectAll}
        />
    );
});
